import { CaretRight, Storefront } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

import { useGetShopServicesQuery, type ShopServiceDto } from '@entities/shopService';
import { haptics } from '@shared/lib/telegram';
import { Card } from '@shared/ui/Card';
import { EmptyState } from '@shared/ui/EmptyState';
import { Spinner } from '@shared/ui/Spinner';
import styles from './ServicesPage.module.css';

/** Where each feature card leads. Slugs are stable, so this map is safe. */
const ROUTES: Record<string, string> = {
  sms: '/services/sms',
};

/**
 * The shop's features, listed as cards.
 *
 * They are not products — SMS is a live browse of upstream stock — but they
 * are presented as products because that is what they are to a customer.
 * Everything here is data: name, blurb, artwork and section all come from the
 * server, so a new feature is a row rather than a release.
 */
export function ServicesPage({ categoryId }: { categoryId?: string | null }) {
  const { data: services = [], isLoading } = useGetShopServicesQuery();
  const navigate = useNavigate();

  const visible =
    categoryId == null ? services : services.filter((s) => s.category?.id === categoryId);

  if (isLoading) return <Spinner label="Loading services" />;

  if (visible.length === 0) {
    return (
      <EmptyState
        icon={Storefront}
        title="No services yet"
        description="Nothing here right now. Please check back soon."
      />
    );
  }

  const open = (service: ShopServiceDto) => {
    haptics.tap('light');
    navigate(ROUTES[service.slug] ?? '/');
  };

  return (
    <ul className={styles.list}>
      {visible.map((service) => (
        <li key={service.slug}>
          <Card className={styles.card} onClick={() => open(service)}>
            <img className={styles.logo} src={service.logoUrl} alt="" loading="lazy" />
            <div className={styles.body}>
              <p className={styles.name}>{service.name}</p>
              {service.description ? (
                <p className={styles.description}>{service.description}</p>
              ) : null}
            </div>
            <CaretRight size={18} className={styles.chevron} />
          </Card>
        </li>
      ))}
    </ul>
  );
}
